import { useId } from "@reach/auto-id"
import {
  Fragment,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { Field, Form } from "react-final-form"

import {
  Button,
  FieldError,
  FormControl,
  Input,
} from "@trueskin-web/components"
import { getVoucherError } from "@trueskin-web/functions"
import { Trans, useTranslation } from "@trueskin-web/translations"

export const InputControl = ({
  input,
  meta,
  required,
  className,
  ...props
}) => {
  const id = useId()

  return (
    <Fragment>
      <FormControl
        isInvalid={(meta.error || meta.submitError) && meta.touched}
        isRequired={required}
        className={className}
      >
        <Input
          id={id}
          size="lg"
          {...input}
          {...props}
          sx={{
            borderColor: "brandNeutralsMediumGrey",
          }}
        />
      </FormControl>
      <FieldError
        data-testid="web-portal-form-error-message"
        sx={{ gridArea: "error", lineHeight: 1.5 }}
      >
        {meta.touched && (meta.error || meta.submitError)}
      </FieldError>
    </Fragment>
  )
}

const VoucherInput = forwardRef(
  ({ voucher, addVoucher, removeVoucher }, ref) => {
    const { t } = useTranslation()

    const formRef = useRef(null)
    useImperativeHandle(ref, () => ({
      resetVoucherValidation: () => {
        if (!formRef?.current) return
        const fieldState = formRef.current.getFieldState("voucher")
        formRef.current.restart({ voucher: fieldState.value })
      },
    }))

    const [showForm, setShowForm] = useState(false)
    const [isEditVoucherLoading, setIsEditVoucherLoading] = useState()

    const editVoucher = useCallback(async () => {
      setIsEditVoucherLoading(true)

      if (voucher) {
        await removeVoucher(voucher.code)
      }

      setIsEditVoucherLoading(false)
      setShowForm(false)
    }, [voucher, removeVoucher])

    const handleSubmit = useCallback(
      async (values) => {
        try {
          const voucherCode = values.voucher?.trim()
          if (voucherCode) {
            await addVoucher(voucherCode, values.voucher)
            setShowForm(false)
          }
        } catch (error) {
          return {
            voucher: getVoucherError({
              ...error,
              voucher: values.voucher,
              components: {
                action: Button,
              },
            }),
          }
        }
      },
      [addVoucher]
    )

    if (voucher) {
      return (
        <div
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
            lineHeight: 1,
          }}
        >
          <div
            data-testid="web-portal-voucher-message"
            sx={{
              mr: 1,
              fontSize: 1,
              color: "textDark",
            }}
          >
            {voucher.code}
          </div>
          <Button
            data-testid="web-portal-voucher-remove-button"
            size="sm"
            variant="link"
            sx={{
              p: 0,
              color: "textDark",
            }}
            onClick={editVoucher}
            isLoading={isEditVoucherLoading}
          >
            <Trans i18nKey="Voucher.removeButton" />
          </Button>
        </div>
      )
    }

    if (!showForm) {
      return (
        <Button
          data-testid="web-portal-voucher-show-form-button"
          variant="link"
          size="sm"
          sx={{
            mt: 4,
            p: 0,
            color: "primary",
          }}
          onClick={() => setShowForm(true)}
        >
          <Trans i18nKey="Voucher.showFormButton" />
        </Button>
      )
    }

    return (
      <Form
        initialValues={{
          voucher: "",
        }}
        onSubmit={handleSubmit}
        render={({
          handleSubmit,
          submitting,
          form,
          hasSubmitErrors,
          values,
        }) => {
          formRef.current = form
          return (
            <form
              onSubmit={handleSubmit}
              sx={{
                mt: 4,
                display: "grid",
                gridTemplateAreas: `"input input input input button" "error error error error error"`,
              }}
            >
              <Field
                data-testid="web-portal-voucher-input"
                name="voucher"
                component={InputControl}
                label={t("Voucher.label")}
                placeholder={t("Voucher.placeholder")}
                size="md"
                rounded
                onChange={(event) => {
                  form.change("voucher", event.target.value)
                  if (hasSubmitErrors) {
                    form.restart({ voucher: event.target.value })
                  }
                }}
                sx={{
                  gridArea: "input",
                  mr: 2,
                }}
              />

              <Button
                data-testid="web-portal-voucher-submit-button"
                type="submit"
                isDisabled={!values.voucher?.trim()}
                isLoading={submitting}
                rounded
                sx={{ gridArea: "button" }}
              >
                <Trans i18nKey="Voucher.submitButton" />
              </Button>
            </form>
          )
        }}
      />
    )
  }
)

VoucherInput.displayName = "VoucherInput"

export default VoucherInput
